import React, { useEffect, useId, useRef, useState } from 'react';
import './App.css';

const CONTACT_EMAIL = 'bdactechlabs@gmail.com';

const publicAsset = (filename: string) => `${process.env.PUBLIC_URL ?? ''}/${filename}`;

function BrandLogoHeader(): React.ReactElement {
  return (
    <picture>
      <source media="(max-width: 768px)" srcSet={publicAsset('small-logo.png')} />
      <img
        className="logo-img logo-img--header"
        src={publicAsset('logo.png')}
        alt="BDAC TECH LABS, C.A."
        decoding="async"
      />
    </picture>
  );
}

function ContactModal({ onClose }: { onClose: () => void }): React.ReactElement {
  const titleId = useId();
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const subject = 'Solicitud de información — BDAC TECH LABS';

    let body = `Nombre: ${name}\nCorreo: ${email}\n`;
    if (organization.trim()) body += `Organización: ${organization}\n`;
    body += `\nMensaje:\n${message}`;

    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-header">
          <h2 className="modal-title" id={titleId}>
            Solicitar información
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>
        <p className="modal-lead">
          Escribe tu mensaje y lo enviaremos a{' '}
          <a className="modal-email-link" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <form className="modal-form" onSubmit={handleSubmit}>
          <label className="form-field">
            <span className="form-label">Nombre</span>
            <input
              ref={firstFieldRef}
              className="form-input"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(ev) => setName(ev.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-label">Correo electrónico</span>
            <input
              className="form-input"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-label">Organización (opcional)</span>
            <input
              className="form-input"
              name="organization"
              type="text"
              autoComplete="organization"
              value={organization}
              onChange={(ev) => setOrganization(ev.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-label">¿En qué podemos ayudarte?</span>
            <textarea
              className="form-textarea"
              name="message"
              required
              rows={4}
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
            />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              Enviar por correo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const App: React.FC = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    if (contactModalOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [contactModalOpen]);

  return (
    <div className="export-wrapper">
      <header className="header container">
        <a href="#inicio" className="logo" aria-label="BDAC TECH LABS, C.A. — Inicio">
          <span className="logo-mark logo-mark--header">
            <BrandLogoHeader />
          </span>
        </a>
        <button className="menu-btn" aria-label="Abrir menú">
          ☰
        </button>
        <nav className="nav-links">
          <a href="#servicios" className="nav-link">
            Servicios
          </a>
          <a href="#nosotros" className="nav-link">
            Nosotros
          </a>
          <a href="#talento" className="nav-link">
            Talento
          </a>
        </nav>
        <div className="header-actions">
          <a href="#contacto" className="btn btn-sm btn-outline">
            Contacto
          </a>
          <a href="#contacto" className="btn btn-sm btn-primary">
            Hablemos de tu proyecto
          </a>
        </div>
      </header>

      <main className="container">
        <section className="hero" id="inicio">
          <div className="hero-content">
            <h1 className="heading-1">
              Software móvil y web
              <br />
              que impulsa a tu organización.
            </h1>
            <p className="text-lg text-muted">
              Desarrollamos aplicaciones móviles y soluciones web, diseñamos herramientas que
              agilizan procesos informáticos y potenciamos a programadores jóvenes para que den lo
              mejor de sí en empresas, instituciones y organizaciones.
            </p>
            <div className="hero-actions">
              <a href="#servicios" className="btn btn-primary">
                Conocer servicios
              </a>
              <a href="#nosotros" className="btn btn-outline">
                Nuestra misión
              </a>
            </div>
          </div>
          <div className="hero-image-wrapper">
            <img
              alt="Equipo de desarrollo colaborando"
              src="https://storage.googleapis.com/banani-generated-images/generated-images/e31ed7ee-a5f0-4f84-8ad8-162bfdd8ea11.jpg"
            />
          </div>
        </section>

        <section className="clients">
          <div className="clients-title">
            Acompañamos a equipos que buscan modernizar sus procesos digitales
          </div>
          <div className="client-logos">
            <div className="client-logo">
              <div className="client-logo-icon">▢</div>
              Empresas
            </div>
            <div className="client-logo">
              <div className="client-logo-icon">▲</div>
              Instituciones
            </div>
            <div className="client-logo">
              <div className="client-logo-icon">◌</div>
              Organizaciones
            </div>
            <div className="client-logo">
              <div className="client-logo-icon">⬡</div>
              Equipos TI
            </div>
            <div className="client-logo">
              <div className="client-logo-icon">☁</div>
              Proyectos digitales
            </div>
          </div>
        </section>

        <section className="section section-secondary" id="servicios">
          <div className="services-header">
            <h2 className="heading-2">Lo que hacemos en BDAC TECH LABS</h2>
            <p className="text-lg text-muted">
              Tres líneas de trabajo que resumen nuestra propuesta: productos digitales sólidos,
              herramientas que simplifican el día a día y un compromiso real con el talento joven.
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">📱</div>
              <h3 className="heading-3">Apps móviles y soluciones web</h3>
              <p className="text-muted">
                Desarrollo de aplicaciones móviles y plataformas web a la medida de tu operación,
                con enfoque en usabilidad, rendimiento y mantenimiento a largo plazo.
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">⚙</div>
              <h3 className="heading-3">Herramientas que agilizan procesos</h3>
              <p className="text-muted">
                Diseño de software interno, automatizaciones e integraciones que reducen fricción en
                tareas repetitivas y mejoran la eficiencia de tus equipos informáticos.
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">◇</div>
              <h3 className="heading-3">Talento joven al servicio del impacto</h3>
              <p className="text-muted">
                Aprovechamos el talento de programadores jóvenes para que desarrollen su máxima
                capacidad y aporten valor tangible a organizaciones, instituciones y empresas.
              </p>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="feature-row" id="nosotros">
            <div className="feature-image">
              <img
                alt="Panel de herramientas y procesos"
                src="https://storage.googleapis.com/banani-generated-images/generated-images/015f4742-034a-477d-985a-f6dc233677c0.jpg"
              />
            </div>
            <div className="feature-content">
              <h2 className="heading-2">Tecnología alineada a tus procesos</h2>
              <p className="text-lg text-muted">
                No solo entregamos código: entendemos flujos de trabajo y construimos soluciones que
                encajan con la forma en que tu equipo ya opera, con espacio para crecer.
              </p>
              <div className="feature-list">
                <div className="feature-list-item">
                  <div className="feature-list-icon">✓</div>
                  Aplicaciones móviles y web pensadas para usuarios reales
                </div>
                <div className="feature-list-item">
                  <div className="feature-list-icon">✓</div>
                  Herramientas que acortan tiempos y reducen errores manuales
                </div>
                <div className="feature-list-item">
                  <div className="feature-list-icon">✓</div>
                  Acompañamiento para integrar lo nuevo con sistemas existentes
                </div>
              </div>
            </div>
          </div>

          <div className="feature-row" id="talento">
            <div className="feature-content">
              <h2 className="heading-2">Formación y confianza en cada entrega</h2>
              <p className="text-lg text-muted">
                Creemos en el potencial del talento emergente. Combinamos mentoría, buenas prácticas
                y comunicación clara para que cada proyecto sea sostenible y transferible.
              </p>
              <div className="feature-list">
                <div className="feature-list-item">
                  <div className="feature-list-icon">✓</div>
                  Equipos jóvenes con supervisión y estándares de calidad
                </div>
                <div className="feature-list-item">
                  <div className="feature-list-icon">✓</div>
                  Transparencia en avances y prioridades con tu organización
                </div>
                <div className="feature-list-item">
                  <div className="feature-list-icon">✓</div>
                  Enfoque en aprendizaje continuo y mejora del producto
                </div>
              </div>
            </div>
            <div className="feature-image">
              <img
                alt="Equipo colaborando en un proyecto"
                src="https://storage.googleapis.com/banani-generated-images/generated-images/747cfb93-4341-4da3-9191-f4ae426ffcc9.jpg"
              />
            </div>
          </div>
        </section>

        <section className="cta-wrapper" id="contacto">
          <div className="cta-box">
            <h2 className="heading-2">¿Tienes un reto digital o un proceso por optimizar?</h2>
            <p className="text-lg cta-text">
              En BDAC TECH LABS, C.A. podemos ayudarte desde una app o un sitio web hasta
              herramientas internas y equipos que crezcan con tu institución o empresa.
            </p>
            <div className="cta-actions">
              <button
                type="button"
                className="btn btn-inverted"
                onClick={() => setContactModalOpen(true)}
              >
                Solicitar información
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="container">
        <div className="footer">
          <div className="footer-col">
            <a href="#inicio" className="logo logo--footer" aria-label="BDAC TECH LABS, C.A. — Inicio">
              <span className="logo-mark logo-mark--footer">
                <img
                  className="logo-img logo-img--footer"
                  src={publicAsset('small-logo.png')}
                  alt="BDAC TECH LABS, C.A."
                  decoding="async"
                />
              </span>
            </a>
            <p className="footer-desc">
              BDAC TECH LABS, C.A. — desarrollo móvil y web, herramientas para procesos informáticos
              y compromiso con el talento joven al servicio de organizaciones e instituciones.
            </p>
          </div>
          <div className="footer-col">
            <div className="footer-heading">Sitio</div>
            <div className="footer-links">
              <a href="#servicios">Servicios</a>
              <a href="#nosotros">Nosotros</a>
              <a href="#talento">Talento</a>
              <a href="#contacto">Contacto</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div>© 2026 BDAC TECH LABS, C.A. Todos los derechos reservados.</div>
        </div>
      </footer>

      {contactModalOpen ? (
        <ContactModal onClose={() => setContactModalOpen(false)} />
      ) : null}
    </div>
  );
};

export default App;
